import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Container,
  Grid,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { Teams } from "src/_mock/teams";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { createBlobInContainer } from "src/utils/azureBlob";
import TeamFormFields from "src/components/teams/TeamFormField";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlayersTable from "src/components/players/PlayersTable";
import { Players } from "src/_mock/players";
import PlayerMenu from "src/components/teams/PlayerMenu";
import PlayersToolbar from "src/components/teams/PlayersToolbar";

const TeamsPageDetail = () => {
  const inputStyles = {
    borderRadius: "50px",
    boxShadow: "0.5px 3px 10px 0px #7777771A",
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "50px",
      borderColor: "#ccc",
    },
    "& .MuiInputBase-input": {
      color: "#6A707E",
      fontSize: "16px",
      fontWeight: 600,
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#6A707E",
    },
    "& .MuiInputLabel-root": {
      color: "#6A707E",
      fontSize: "16px",
      fontWeight: 600,
    },
  };
  const { teamsId } = useParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPlayerId, setMenuPlayerId] = useState(null);
  const [selectedLeague] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");
  const [players, setPlayers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setPlayers(Players);
  }, []);

  const [editedTeam, setEditedTeam] = useState(() => {
    if (!teamsId || teamsId === "new") {
      return {
        id: Date.now(),
        teamName: "",
        nickname: "",
        sport: "",
        league: "",
        triCode: "",
        players: 0,
        country: "",
        logo: "",
        city: "",
      };
    }

    const foundTeam = Teams.find((p) => p.id.toString() === teamsId);
    return (
      foundTeam || {
        id: Date.now(),
        teamName: "",
        nickname: "",
        sport: "",
        league: "",
        triCode: "",
        players: 0,
        country: "",
        logo: "",
        city: "",
      }
    );
  });

  if (!editedTeam && teamsId !== "new") {
    return (
      <Container>
        <Typography variant="h6">Team not found</Typography>
      </Container>
    );
  }
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedTeam((prevTeam) => ({
      ...prevTeam,
      [name]: value,
    }));
  };

  const filteredPlayers = players.filter((player) => {
    return (
      (player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player.nickname.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (selectedLeague === "" || player.league === selectedLeague) &&
      (selectedPosition === "" || player.position === selectedPosition)
    );
  });

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPlayerId(null);
  };
  const handleAdd = () => {
    navigate("/dashboard/players/new");
  };

  const handleEdit = (playerId) => {
    navigate(`/dashboard/players/${playerId}`);
    handleMenuClose();
  };

  const handleDelete = () => {
    handleMenuClose();
  };

  const handleMenuOpen = (event, playerId) => {
    setAnchorEl(event.currentTarget);
    setMenuPlayerId(playerId);
  };

  const uploadFiles = (event) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const imageType = event.target.dataset.imageType;
      if (!file || !imageType) return;

      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
      const maxSize = 2 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        alert("Only JPG, JPEG, PNG, WEBP, and GIF formats are allowed.");
        event.target.value = null;
        return;
      }

      if (file.size > maxSize) {
        alert("File size should not exceed 2MB.");
        event.target.value = null;
        return;
      }

      const newFileName = file.name.replace(/\s+/g, "_");
      const extension = newFileName.substring(newFileName.lastIndexOf("."));
      const safeFileName = `image_${Date.now()}${extension}`;

      const renamedFile = new File([file], safeFileName, { type: file.type });

      const uploadFilesAsync = async () => {
        try {
          await createBlobInContainer(renamedFile);
          const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
          const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
          const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${renamedFile.name}`;

          setEditedTeam((prevTeam) => ({
            ...prevTeam,
            [imageType]: imageUrl,
          }));
        } catch (err) {
          alert("Error uploading file");
        } finally {
          event.target.value = null;
        }
      };

      uploadFilesAsync();
    }
  };

  const handleSave = () => {
    navigate("/dashboard/assets/teams");
  };

  const handleCancel = () => {
    navigate("/dashboard/assets/teams");
  };

  return (
    <Container maxWidth="lg" sx={{ display: "flex", gap: 4, py: 4 }}>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ mb: 5, display: "flex", alignItems: "center" }}
        >
          Teams
          {teamsId !== "new" && editedTeam.teamName && (
            <>
              <ArrowForwardIosIcon sx={{ mx: 1 }} />
              {editedTeam.teamName}
            </>
          )}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 3,
            paddingTop: "40px",
          }}
        >
          <TeamFormFields
            editedTeam={editedTeam}
            handleInputChange={handleInputChange}
          />
          <Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              {[
                {
                  label: "Logo",
                  imageType: "logo",
                  src: editedTeam.logo,
                },
                {
                  label: "Avatar",
                  imageType: "avatar",
                  src: editedTeam.avatar,
                },
                {
                  label: "Alt Image",
                  imageType: "altImage",
                  src: editedTeam.altImage,
                },
                {
                  label: "Country Flag",
                  imageType: "altFlag",
                  src: editedTeam.altFlag,
                },
              ].map(({ label, imageType, src }) => (
                <Box key={imageType}>
                  <Typography
                    sx={{ mb: 2, fontWeight: "500", fontSize: 16 }}
                    align="center"
                  >
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      width: 190,
                      height: 191,
                      bgcolor: "#FFFFFF",
                      borderRadius: "21px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    {src ? (
                      <img
                        alt={label}
                        src={src}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        No image
                      </Typography>
                    )}
                  </Box>
                  <Button component="label" fullWidth>
                    Upload
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      hidden
                      data-image-type={imageType}
                      onChange={uploadFiles}
                    />
                  </Button>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 10, paddingTop: 5 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: 300,
                }}
              >
                <TextField
                  select
                  label="+ Add League"
                  fullWidth
                  variant="outlined"
                  size="small"
                  SelectProps={{
                    native: false,
                    IconComponent: KeyboardArrowDownIcon,
                    sx: { borderRadius: "50px" },
                  }}
                  InputProps={{
                    sx: { borderRadius: "50px" },
                  }}
                />

                <Box marginTop={3}>
                  <TextField
                    label="+ Add Feed ID"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      sx: { borderRadius: "50px" },
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: 250,
                }}
              >
                <TextField
                  select
                  label="+ Add League"
                  fullWidth
                  variant="outlined"
                  size="small"
                  SelectProps={{
                    native: false,
                    IconComponent: KeyboardArrowDownIcon,
                    sx: { borderRadius: "50px" },
                  }}
                  InputProps={{
                    sx: { borderRadius: "50px" },
                  }}
                />

                <Box marginTop={3}>
                  <TextField
                    label="+ Add Feed ID"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      sx: { borderRadius: "50px" },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {teamsId !== "new" && editedTeam.teamName && (
          <>
            <PlayersToolbar
              players={players}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedPosition={selectedPosition}
              setSelectedPosition={setSelectedPosition}
              handleAdd={handleAdd}
              inputStyles={inputStyles}
              teamName={editedTeam.teamName}
            />

            <PlayersTable
              players={filteredPlayers}
              onMenuOpen={handleMenuOpen}
            />

            <PlayerMenu
              anchorEl={anchorEl}
              onClose={handleMenuClose}
              onEdit={handleEdit}
              onDelete={handleDelete}
              playerId={menuPlayerId}
            />
          </>
        )}
      </Box>
    </Container>
  );
};

export default TeamsPageDetail;
